import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { ArrowLeft, Share2, Link2, Check, Printer } from "lucide-react";
import MessageForm from "../components/MessageForm";
import { FavoriteButton } from "../components/FavoriteButton";
import { VoiceAudioPlayer } from "../components/VoiceAudioPlayer";
import { accentForType } from "../lib/typeAccent";
import { Seo } from "../components/Seo";
import { buildDescription } from "../lib/seo";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function TextDetail() {
  const { id } = useParams();
  const [text, setText] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/texts/${id}`);
        setText(data);
        const seenKey = `viewed:${id}`;
        if (!sessionStorage.getItem(seenKey)) {
          sessionStorage.setItem(seenKey, "1");
          api.post(`/texts/${id}/view`).catch(() => {});
        }
      } catch {
        setError("Texto no encontrado.");
      }
    })();
  }, [id]);

  if (error) {
    return (
      <div
        className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center font-display italic text-2xl"
        style={{ color: "var(--rincon-text-muted)" }}
        data-testid="text-detail-error"
      >
        {error}
      </div>
    );
  }

  if (!text) {
    return (
      <div
        className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center font-display italic text-2xl"
        style={{ color: "var(--rincon-text-muted)" }}
      >
        Cargando...
      </div>
    );
  }

  const seoOrigin =
    typeof window !== "undefined" ? window.location.origin : "https://rincon.javiloboautor.com";
  const seoUrl = `${seoOrigin}/texto/${text.id}`;
  const seoDescription = buildDescription(text.epigraph || text.content || "");
  const seoImage = text.image
    ? `${API}/texts/${text.id}/image`
    : `${seoOrigin}/og-default.jpg`;
  const seoCategory = `${text.main_category} · ${text.subcategory}`;
  const seoJsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: text.title,
    headline: text.title,
    description: seoDescription,
    inLanguage: "es",
    url: seoUrl,
    datePublished: text.created_at,
    dateModified: text.updated_at || text.created_at,
    author: { "@type": "Person", name: "Javilobo", url: `${seoOrigin}/autor` },
    publisher: { "@type": "Organization", name: "El rincón de mis palabras", url: seoOrigin },
    articleSection: seoCategory,
    image: seoImage,
  };

  return (
    <>
    <Seo
      title={text.title}
      description={seoDescription}
      image={seoImage}
      url={seoUrl}
      type="article"
      jsonLd={seoJsonLd}
    />
    <article
      className="max-w-3xl mx-auto px-6 md:px-12 pt-14 pb-32 fade-in-up"
      data-testid="article-content"
    >
      <Link
        to={`/${text.main_category.toLowerCase()}`}
        className="inline-flex items-center gap-2 text-sm ink-link mb-12 no-print"
        style={{ color: "var(--rincon-text-muted)" }}
        data-testid="back-link"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Volver a {text.main_category}
      </Link>

      <div
        className="h-1 w-20 mb-6"
        style={{ background: accentForType(text.type) }}
        aria-hidden="true"
      />

      <div
        className="text-xs uppercase tracking-[0.35em] mb-6 font-semibold"
        style={{ color: accentForType(text.type) }}
      >
        {text.main_category} · {text.subcategory}
      </div>

      <h1
        className="font-display font-light text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight"
        style={{ color: "var(--rincon-text)" }}
        data-testid="article-title"
      >
        {text.title}
      </h1>

      {text.epigraph ? (
        <div
          className="mt-6 font-body italic text-lg md:text-xl max-w-xl"
          style={{ color: "var(--rincon-secondary)" }}
          data-testid="article-epigraph"
        >
          «{text.epigraph}»
        </div>
      ) : null}

      <div
        className="mt-6 font-body italic text-base"
        style={{ color: "var(--rincon-text-muted)" }}
      >
        {formatDate(text.created_at)}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3 no-print">
        <FavoriteButton text={text} showLabel />
        <button
          type="button"
          onClick={() => window.print()}
          data-testid="article-print-button"
          className="inline-flex items-center gap-2 font-display text-lg transition-colors hover:opacity-80"
          style={{ color: "var(--rincon-text-muted)" }}
        >
          <Printer className="w-4 h-4" /> Imprimir
        </button>
      </div>

      {(text.audio_url || (text.audios && Object.values(text.audios).some(Boolean))) ? (
        <div className="mt-10 no-print" data-testid="article-audio">
          <div
            className="text-xs uppercase tracking-[0.25em] mb-3 font-semibold"
            style={{ color: "var(--rincon-text-muted)" }}
          >
            Recital · escucha al autor
          </div>
          <VoiceAudioPlayer
            audios={text.audios}
            audioUrl={text.audio_url}
            idBase={text.id}
          />
          <a
            href={`/poemario/index.html#poema=${text.id}`}
            data-testid="article-poemario-link"
            className="inline-flex items-center gap-2 mt-4 text-sm font-body italic transition-colors hover:opacity-70"
            style={{ color: "var(--rincon-secondary)" }}
          >
            ❦ Escuchar en El Poemario, con música →
          </a>
        </div>
      ) : null}

      {text.image ? (
        <div className="mt-12" data-testid="article-image">
          <img
            src={text.image}
            alt={text.title}
            className="w-full h-auto"
            style={{ filter: "sepia(0.03)" }}
          />
        </div>
      ) : null}

      <div
        className="mt-12 border-t pt-12"
        style={{ borderColor: "var(--rincon-border)" }}
      >
        <div
          className={
            text.type === "poema"
              ? "font-body whitespace-pre-wrap text-xl md:text-2xl leading-[1.8] text-center"
              : "prose-literary font-body whitespace-pre-wrap"
          }
          style={{ color: "var(--rincon-text)" }}
        >
          {text.content}
        </div>
      </div>

      {text.tags && text.tags.length > 0 ? (
        <div
          className="mt-16 pt-8 border-t flex flex-wrap gap-x-4 gap-y-2"
          style={{ borderColor: "var(--rincon-border)" }}
        >
          {text.tags.map((tag) => (
            <span
              key={tag}
              className="text-sm italic"
              style={{ color: "var(--rincon-secondary)" }}
              data-testid={`tag-${tag}`}
            >
              #{tag}
            </span>
          ))}
        </div>
      ) : null}

      {/* Share */}
      <div
        className="mt-16 pt-10 border-t no-print"
        style={{ borderColor: "var(--rincon-border)" }}
        data-testid="share-section"
      >
        {(() => {
          const shareUrl = `${API}/share/texts/${text.id}`;
          const title = text.title;
          const text_ = text.epigraph || `${title} — El rincón de mis palabras`;
          const enc = encodeURIComponent;
          const accent = accentForType(text.type);
          const links = [
            {
              key: "whatsapp",
              label: "WhatsApp",
              href: `https://wa.me/?text=${enc(`${title} — ${shareUrl}`)}`,
            },
            {
              key: "twitter",
              label: "X / Twitter",
              href: `https://twitter.com/intent/tweet?text=${enc(text_)}&url=${enc(shareUrl)}`,
            },
            {
              key: "facebook",
              label: "Facebook",
              href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
            },
          ];
          const doCopy = async () => {
            try {
              await navigator.clipboard.writeText(shareUrl);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch (e) {
              console.error("No se pudo copiar el enlace:", e);
            }
          };
          const tintIn = (e) => (e.currentTarget.style.color = accent);
          const tintOut = (e) => (e.currentTarget.style.color = "var(--rincon-primary)");
          return (
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <div
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em]"
                style={{ color: "var(--rincon-text-muted)" }}
              >
                <Share2 className="w-3.5 h-3.5" />
                Comparte este texto
              </div>
              {links.map((l) => (
                <a
                  key={l.key}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`share-${l.key}`}
                  className="font-display text-lg ink-link transition-colors"
                  style={{ color: "var(--rincon-primary)" }}
                  onMouseEnter={tintIn}
                  onMouseLeave={tintOut}
                  onFocus={tintIn}
                  onBlur={tintOut}
                >
                  {l.label}
                </a>
              ))}
              <button
                type="button"
                onClick={doCopy}
                data-testid="share-copy"
                className="inline-flex items-center gap-2 font-display text-lg ink-link transition-colors"
                style={{ color: "var(--rincon-primary)" }}
                onMouseEnter={tintIn}
                onMouseLeave={tintOut}
                onFocus={tintIn}
                onBlur={tintOut}
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5" /> Enlace copiado
                  </>
                ) : (
                  <>
                    <Link2 className="w-3.5 h-3.5" /> Copiar enlace
                  </>
                )}
              </button>
            </div>
          );
        })()}
      </div>

      <div className="no-print">
        <MessageForm textId={text.id} />
      </div>
    </article>
    </>
  );
}
